/**
 * Jeedigunta Puraskaralu — Score Computation Utilities
 *
 * Business Rules:
 *   Voice Score = avg(shruti, modulation, uccharana, gati)
 *   Story Score = avg(rating_story, rating_writer, readability, vastu, shilpam, innovation)
 *   గళకళ       = 0.75 * Voice Score + 0.25 * Story Score
 *   కలం-గళం    = 0.75 * Story Score + 0.25 * Voice Score
 *
 * Multi-judge aggregation:
 *   1. Average each criterion across all judges
 *   2. Compute Voice/Story scores from averaged criteria
 *   3. Apply category formula
 */

// All 10 scoring criteria field names
export const VOICE_FIELDS = ["shruti", "modulation", "uccharana", "gati"];
export const STORY_FIELDS = [
  "rating_story",
  "rating_writer",
  "readability",
  "vastu",
  "shilpam",
  "innovation",
];
export const ALL_FIELDS = [...VOICE_FIELDS, ...STORY_FIELDS];

// Dropdown options: 0, 0.5, 1.0, ..., 10.0
export const SCORE_OPTIONS = Array.from({ length: 21 }, (_, i) =>
  (i * 0.5).toFixed(1)
);

// Telugu labels for each criterion
export const CRITERIA_LABELS = {
  shruti: { telugu: "శ్రుతి", english: "Shruti" },
  modulation: { telugu: "మాడ్యులేషన్", english: "Modulation" },
  uccharana: { telugu: "ఉచ్చారణ", english: "Uccharana" },
  gati: { telugu: "గతి", english: "Gati" },
  rating_story: { telugu: "కథ", english: "Story" },
  rating_writer: { telugu: "రచన", english: "Writer" },
  readability: { telugu: "రీడబిలిటీ", english: "Readability" },
  vastu: { telugu: "వస్తు", english: "Vastu" },
  shilpam: { telugu: "శిల్పం", english: "Shilpam" },
  innovation: { telugu: "ఇన్నొవేషన్", english: "Innovation" },
};

/**
 * Average of non-null values from given fields on a score row.
 * Returns null if any field is null (= ineligible).
 */
function avgFields(scoreRow, fields) {
  const vals = fields.map((f) => parseFloat(scoreRow[f]));
  if (vals.some((v) => isNaN(v) || v === null || v === undefined)) return null;
  return vals.reduce((sum, v) => sum + v, 0) / vals.length;
}

/** Voice Score for a single score row */
export function voiceScore(scoreRow) {
  return avgFields(scoreRow, VOICE_FIELDS);
}

/** Story Score for a single score row */
export function storyScore(scoreRow) {
  return avgFields(scoreRow, STORY_FIELDS);
}

/** Sum of all 10 criteria (for display total) */
export function totalScore(scoreRow) {
  const vals = ALL_FIELDS.map((f) => parseFloat(scoreRow[f]));
  if (vals.some((v) => isNaN(v))) return null;
  return vals.reduce((sum, v) => sum + v, 0);
}

/** Check if all 10 scores are filled (non-null) */
export function isComplete(scoreRow) {
  return ALL_FIELDS.every((f) => {
    const v = parseFloat(scoreRow[f]);
    return !isNaN(v);
  });
}

/** గళకళ composite: 75% Voice + 25% Story */
export function galakalaComposite(vs, ss) {
  if (vs === null || ss === null) return null;
  return 0.75 * vs + 0.25 * ss;
}

/** కలం-గళం composite: 75% Story + 25% Voice */
export function kalamGalamComposite(vs, ss) {
  if (vs === null || ss === null) return null;
  return 0.75 * ss + 0.25 * vs;
}

/**
 * Average a single criterion across multiple judge score rows.
 * Only includes rows where the value is non-null.
 */
export function avgCriterion(scoreRows, field) {
  const vals = scoreRows
    .map((r) => parseFloat(r[field]))
    .filter((v) => !isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((sum, v) => sum + v, 0) / vals.length;
}

/**
 * Compute composite score for a performance across all judges.
 * Multi-judge aggregation order:
 *   1. Average each criterion across judges
 *   2. Compute Voice/Story scores from averaged criteria
 *   3. Apply category formula
 *
 * @param {Array} judgeScoreRows - All judge_scores rows for one performance
 * @param {string} category - 'galakala' | 'kalam_galam' | 'toli_kathanam_galam' | 'toli_kathanam_kalam'
 * @returns {{ voiceScore, storyScore, composite, avgCriteria }}
 */
export function computeComposite(judgeScoreRows, category) {
  // Only include complete score rows
  const complete = judgeScoreRows.filter(isComplete);
  if (complete.length === 0)
    return { voiceScore: null, storyScore: null, composite: null, avgCriteria: {} };

  // Step 1: Average each criterion across judges
  const avgCriteria = {};
  for (const f of ALL_FIELDS) {
    avgCriteria[f] = avgCriterion(complete, f);
  }

  // Step 2: Compute Voice/Story scores from averaged criteria
  const vs = avgFields(avgCriteria, VOICE_FIELDS);
  const ss = avgFields(avgCriteria, STORY_FIELDS);

  // Step 3: Apply category formula
  let composite;
  if (category === "galakala" || category === "toli_kathanam_galam") {
    composite = galakalaComposite(vs, ss);
  } else {
    composite = kalamGalamComposite(vs, ss);
  }

  return { voiceScore: vs, storyScore: ss, composite, avgCriteria };
}
